import React, { useState, useRef } from "react";
import { axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { useQueryClient } from "@tanstack/react-query";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { FiGift, FiX, FiInfo } from "react-icons/fi";

const AcceptanceWaiver = ({ v }) => {
  const [showModal, setShowModal] = useState(false);
  const [codes, setCodes] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const queryClient = useQueryClient();

  const handleCodeChange = (index, value) => {
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (sanitized.length <= 1) {
      const newCodes = [...codes];
      newCodes[index] = sanitized;
      setCodes(newCodes);
      if (sanitized && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    const newCodes = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
    setCodes(newCodes);
    const nextEmptyIndex = newCodes.findIndex((code) => !code);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleApply = async () => {
    try {
      const waiverCode = codes.join("");
      if (waiverCode.length !== 6) {
        swal("error", "Please enter a valid 6-character waiver code");
        return;
      }
      setLoading(true);
      const req = await axiosput(`ms_waiver/${waiverCode}/acceptance`, {
        applicationId: v?._id,
      });
      const result = req?.data || req;
      if (result?.type === "success") {
        swal("success", result.message);
        setShowModal(false);
        setCodes(["", "", "", "", "", ""]);
        queryClient.refetchQueries({ queryKey: ["acceptanceInformation"] });
      } else {
        swal("error", result?.message || result?.error || "Failed to apply waiver code");
      }
      setLoading(false);
    } catch (error) {
      swal("error", error?.message || "An error occurred while applying the waiver code");
      setLoading(false);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setCodes(["", "", "", "", "", ""]);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
      >
        <FiGift className="text-lg" />
        <span className="text-lg">Pay with Waiver</span>
      </button>

      <ReactModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          <div className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiGift className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-lg font-semibold text-white">Apply Waiver Code</h5>
                <p className="text-amber-100 text-sm mt-0.5">Enter your code to waive acceptance fee</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-white/20 transition-colors">
                <FiX className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <div className="flex items-start gap-2">
                <FiInfo className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">Enter your 6-character waiver code to bypass acceptance fee payment.</p>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 text-center mb-4">Enter Waiver Code</label>
              <div className="flex justify-center gap-2.5">
                {codes.map((code, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={code}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => handlePaste(e)}
                    className="w-12 h-14 text-center text-xl font-bold font-mono uppercase rounded-lg border-2 border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-white text-neutral-900"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleApply}
                disabled={loading}
                className={`flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-200 ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {loading ? (
                  <><i className="fa fa-spinner fa-spin"></i> Applying...</>
                ) : (
                  <><FiGift className="h-4 w-4" /> Apply Waiver</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </ReactModal>
    </>
  );
};

export default AcceptanceWaiver;
